import { Box, Flex, UseDisclosureReturn } from '@chakra-ui/react';
import { EntityNoteModel } from '../../globalModels/entityNotes/entityNoteModel';
import { BaseFormPopover, createFieldHelper, useHookForm, useStandardToast } from '@cks-internal/react-base';
import { useEntityNoteMutation } from './apis/useEntityNoteMutation';
import { EntityNoteQueryType } from '../../enums/common/EntityNoteQueryType';
import { PropsWithChildren } from 'react';

type PinNotePopoverProps = PropsWithChildren & {
  disclosure: UseDisclosureReturn;
  model: EntityNoteModel;
  id?: number;
  queryType: EntityNoteQueryType;
  onPinned: () => void;
};

export const PinNotePopover = (props: PinNotePopoverProps) => {
  const toast = useStandardToast();
  const { formMethods } = useHookForm<{ ExpiresOn?: string }>({
    ExpiresOn: undefined
  });
  const helper = createFieldHelper<{ ExpiresOn?: string }>(formMethods);
  const updateNoteMutation = useEntityNoteMutation();

  const fields = [helper.dateInput('ExpiresOn').withLabel('Expires On')];

  const onSubmit = async (formModel: { ExpiresOn?: string }) => {
    const isPinning = !props.model.IsPinned;
    const updatedNote: EntityNoteModel = {
      ...props.model,
      IsPinned: !props.model.IsPinned,
      ExpiresOn: formModel.ExpiresOn
    };
    await updateNoteMutation.mutateAsync({ noteType: props.queryType, id: props.id, model: updatedNote });
    props.disclosure.onClose();
    formMethods.reset();
    props.onPinned();
    toast.successToast('Success', isPinning ? 'Note pinned' : 'Note unpinned');
  };

  return (
    <BaseFormPopover<{ ExpiresOn?: string }>
      trigger={props.children}
      formControlProps={{
        onCancel: () => {
          formMethods.reset();
          props.disclosure.onClose();
        }
      }}
      formMethods={formMethods}
      disclosure={props.disclosure}
      onSubmit={(val) => onSubmit(val)}>
      <Flex gap={4} flexDir={'column'} w={'100%'}>
        <Box fontSize='sm' color='gray.600'>
          {!props.model.IsPinned ? (
            <>
              <Box mb={2}>
                Pin this note to <b>enable</b> it to be displayed to agents.
              </Box>
              <Box mb={2}>
                Optionally set an <b>expiry date </b>to automatically unpin the note.
              </Box>
              <Box color='gray.600'>
                Expires the <b>day of </b>the selected date
              </Box>
            </>
          ) : (
            <>
              <Box mb={2}>
                Unpin this note to <b>hide</b> it from agents.
              </Box>
              <Box color='gray.600'>
                You can optionally set an <b>expiry date</b> to automatically unpin this note.
              </Box>
            </>
          )}
        </Box>
        {!props.model.IsPinned && helper.renderFieldHelperInput('ExpiresOn', fields)}
      </Flex>
    </BaseFormPopover>
  );
};
